import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Box, Container, ThemeProvider, Typography,
} from '@material-ui/core';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { TextField } from '@/components';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import {
  Product, getPdtUid, getProductMinAge,
} from '@/utils/productHelpers';
import {
  validateZipCode, validateEmployeeId, getAge,
} from '@/utils/stringValidators';
import { parseDate, formatDateString } from '@/utils/stringUtils';
import { formatDateInput } from '@/utils/inputHelpers';
import { ELIGIBILITY_CONFIGURATION_QUERY, ME_QUERY } from '@/apollo/queries';
import { EDIT_PATIENT_INFO, CONSUMER_APP_SAVE_COVERAGE_DETAILS } from '@/apollo/mutations';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { requestAuthCode } from '@/utils/sparkActivationApi';
import { REDIRECT_TO_SESSION_KEY, PRODUCT_DEEP_LINKS } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';

const CoverageCheckInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { product, org_slug: orgSlug } = useParams<{ product: Product; org_slug: string }>();
  const { state: locationState } = useLocation<{
    patientUid?: string; phoneNumber?: string; flow?: string;
    accessToken?: string; refreshToken?: string;
  }>();
  const showPhoneField = locationState?.flow === 'email';
  const { t } = useTranslation();

  // TODO: Remove — debug session on mount
  const [mountLog, setMountLog] = useState('checking...');
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session: s } } = await getSupabaseClient().auth.getSession();
      const keys = Object.keys(localStorage).filter((k) => k.includes('supabase')).join(', ') || 'none';
      setMountLog(`session: ${s ? 'YES' : 'NO'} | supabase keys: ${keys}`);
    };
    checkSession();
  }, []);

  // Fetch org eligibility config
  const { data: configData } = useQuery(ELIGIBILITY_CONFIGURATION_QUERY, {
    variables: { organizationSlug: orgSlug, pdtUid: getPdtUid(product) },
    skip: !orgSlug,
    context: locationState?.accessToken
      ? { headers: { Authorization: `Bearer ${locationState.accessToken}` } }
      : undefined,
  });
  const config = configData?.eligibilityConfiguration;
  const showZipCode = config?.collectZipcodeForEligibility ?? false;
  const showEmployeeId = config?.hasEmployeeId ?? false;
  const employeeIdLabel = config?.employeeIdFieldRename || t('coverage.spark.patient.employee_id_label');
  const minAge = getProductMinAge(product);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(locationState?.phoneNumber || '');

  // Error state
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [dobError, setDobError] = useState('');
  const [zipCodeError, setZipCodeError] = useState(false);
  const [employeeIdError, setEmployeeIdError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [isIneligible, setIsIneligible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // TODO: Remove debug state
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const addLog = (msg: string) => setDebugLogs((prev) => [...prev, msg]);

  const [editPatientInfo] = useMutation(EDIT_PATIENT_INFO);
  const [saveCoverageDetails] = useMutation(CONSUMER_APP_SAVE_COVERAGE_DETAILS);
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, { fetchPolicy: 'no-cache' });

  // Validation — dob stored as "MM / DD / YYYY" (14 chars), same as UserInfoConfirmation
  const isDobValid = dob.length === 14 && (() => {
    const parsed = parseDate(dob);
    const date = new Date(parsed);
    const isValidDate = !Number.isNaN(date.getTime()) && date.getFullYear() >= 1900;
    return isValidDate && getAge(dob) >= minAge;
  })();
  const isFormValid = firstName.trim() !== ''
    && lastName.trim() !== ''
    && isDobValid
    && (showZipCode ? validateZipCode(zipCode) : true)
    && (showEmployeeId ? employeeId.trim() !== '' : true);

  const handleSubmit = async () => {
    setGeneralError('');
    setDebugLogs([]);
    addLog(`orgSlug: ${orgSlug}`);
    addLog(`product: ${product}`);
    addLog(`pdtUid: ${getPdtUid(product)}`);
    addLog(`dob: "${dob}" → formatDateString: "${formatDateString(dob)}"`);
    addLog(`showZipCode: ${showZipCode}, zipCode: "${zipCode}"`);
    addLog(`showEmployeeId: ${showEmployeeId}, employeeId: "${employeeId}"`);
    addLog(`showPhoneField: ${showPhoneField}, phoneNumber: "${phoneNumber}"`);
    addLog(`flow: ${locationState?.flow || 'none'}`);

    try {
      setIsSubmitting(true);

      // Get tokens — from location state (email/phone flow) or getSession fallback
      let accessToken = locationState?.accessToken;
      let refreshToken = locationState?.refreshToken;

      if (!accessToken) {
        const { data: { session } } = await getSupabaseClient().auth.getSession();
        accessToken = session?.access_token ?? undefined;
        refreshToken = session?.refresh_token ?? undefined;
        addLog(`session fallback: ${session ? 'YES' : 'NO'}`);
      } else {
        addLog('using tokens from location state');
      }

      if (!accessToken) {
        addLog('ERROR: no access token');
        setGeneralError(t('coverage.errors.generic_error'));
        setIsSubmitting(false);
        return;
      }

      // Single mutation: checks eligibility + saves coverage + updates patient info
      addLog('Calling saveCoverageDetails...');
      const coverageResponse = await saveCoverageDetails({
        variables: {
          pdtUid: getPdtUid(product),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth: formatDateString(dob),
          coverageCheckTimestamp: new Date().toISOString(),
          coverageCheckStatus: 'pass',
          postalCode: zipCode || undefined,
          employeeId: showEmployeeId ? employeeId.trim() : undefined,
        },
        context: {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      });

      addLog(`result: ${JSON.stringify(coverageResponse.data?.consumerAppSaveCoverageDetails)}`);
      addLog(`errors: ${JSON.stringify(coverageResponse.errors || 'none')}`);

      if (!coverageResponse.data?.consumerAppSaveCoverageDetails?.success) {
        addLog('ERROR: not eligible or save failed');
        setIsIneligible(true);
        setIsSubmitting(false);
        return;
      }

      // Save phone if provided (saveCoverageDetails doesn't handle phone)
      if (phoneNumber) {
        addLog('Calling editPatientInfo for phone...');
        await editPatientInfo({
          variables: {
            changeReason: 'updating phone from spark employer activation flow',
            phone: phoneNumber,
          },
          context: {
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        });
      }

      // Session handoff
      if (!refreshToken) {
        addLog('ERROR: no refresh token for handoff');
        setGeneralError(t('coverage.errors.generic_error'));
        setIsSubmitting(false);
        return;
      }

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY)
          || PRODUCT_DEEP_LINKS[product];
      addLog(`redirectTo: ${redirectTo || 'NOT FOUND'}`);
      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        addLog('ERROR: redirectTo missing or invalid');
        setGeneralError(t('coverage.errors.generic_error'));
        setIsSubmitting(false);
        return;
      }

      addLog('Calling requestAuthCode...');
      const { exchangeToken } = await requestAuthCode(accessToken, refreshToken);

      const { data: meData } = await fetchMe({
        context: { headers: { Authorization: `Bearer ${accessToken}` } },
      });
      const limbixUserUid = meData?.me?.uid;

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      addLog(`deep link: ${deepLinkUrl.toString().substring(0, 80)}...`);
      window.location.href = deepLinkUrl.toString();
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      addLog(`ERROR caught: ${errMsg}`);
      setGeneralError(t('coverage.errors.generic_error'));
    }
  };

  if (isIneligible) {
    return (
      <ThemeProvider theme={sparkTheme}>
        <Box display="flex" flexDirection="column" minHeight="100vh">
          <Header />
          <Container maxWidth="xs" style={{ flex: 1, paddingTop: 32 }}>
            <Typography variant="h2" gutterBottom>
              {t('coverage.spark.coverage_rejection.title')}
            </Typography>
            <Typography>
              {t('coverage.spark.coverage_rejection.message')}
            </Typography>
          </Container>
          <Footer />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <Header />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <Box style={{ marginTop: 32, marginBottom: 16 }}>
          <Typography variant="h2" style={{ marginBottom: 8 }} color="textPrimary">
            {t('coverage.spark.patient.coverage_title')}
          </Typography>
          <Typography color="textPrimary">
            {t('coverage.spark.patient.coverage_subtitle')}
          </Typography>
        </Box>

        <Box style={{ marginBottom: 32 }}>
          <TextField
            required
            error={firstNameError}
            helperText={firstNameError ? t('coverage.errors.first_name_error') : ''}
            label={t('coverage.spark.patient.f_name_label')}
            inputProps={{ placeholder: t('coverage.spark.patient.f_name_label'), 'data-testid': 'first-name-input' }}
            value={firstName}
            onChange={(e) => { setFirstNameError(false); setFirstName(e.target.value); }}
            onBlur={(e) => setFirstNameError(e.target.value.trim() === '')}
          />
          <TextField
            required
            error={lastNameError}
            helperText={lastNameError ? t('coverage.errors.last_name_error') : ''}
            label={t('coverage.spark.patient.l_name_label')}
            inputProps={{ placeholder: t('coverage.spark.patient.l_name_label'), 'data-testid': 'last-name-input' }}
            value={lastName}
            onChange={(e) => { setLastNameError(false); setLastName(e.target.value); }}
            onBlur={(e) => setLastNameError(e.target.value.trim() === '')}
          />
          {showZipCode && (
            <TextField
              required
              error={zipCodeError}
              helperText={zipCodeError ? t('coverage.errors.zipcode_error') : ''}
              label={t('coverage.spark.patient.zipcode_label')}
              inputProps={{ placeholder: t('coverage.spark.patient.zipcode_label'), 'data-testid': 'zipcode-input' }}
              value={zipCode}
              onChange={(e) => { setZipCodeError(false); setZipCode(e.target.value); }}
              onBlur={(e) => setZipCodeError(!validateZipCode(e.target.value))}
            />
          )}
          {showEmployeeId && (
            <TextField
              required
              error={!!employeeIdError}
              helperText={employeeIdError}
              label={employeeIdLabel}
              inputProps={{
                placeholder: config?.employeeIdExample || employeeIdLabel,
                'data-testid': 'employee-id-input',
              }}
              value={employeeId}
              onChange={(e) => { setEmployeeIdError(''); setEmployeeId(e.target.value); }}
              onBlur={(e) => {
                const result = validateEmployeeId(e.target.value.trim(), {
                  validationEnabled: config?.employeeIdValidationEnabled,
                  allowedChars: config?.employeeIdAllowedChars,
                  minLength: config?.employeeIdMinLength,
                  maxLength: config?.employeeIdMaxLength,
                  errorMessage: config?.employeeIdValidationErrorMessage,
                });
                if (!result.isValid) {
                  setEmployeeIdError(result.errorMessage || t('coverage.errors.employee_id_error'));
                }
              }}
            />
          )}
          <TextField
            required
            error={!!dobError}
            helperText={dobError}
            label={t('coverage.spark.patient.dob_label')}
            inputProps={{
              placeholder: 'MM / DD / YYYY',
              maxLength: 14,
              inputMode: 'numeric' as const,
              'data-testid': 'dob-input',
            }}
            value={formatDateInput(dob)}
            onChange={(e) => {
              setDobError('');
              const value = formatDateInput(e.target.value);
              setDob(value);
            }}
            onBlur={() => {
              if (dob.length === 14) {
                const parsed = parseDate(dob);
                const date = new Date(parsed);
                const isValidDate = !Number.isNaN(date.getTime()) && date.getFullYear() >= 1900;
                if (isValidDate && getAge(dob) < minAge) {
                  setDobError(t('coverage.spark.patient.dob_age_error'));
                } else if (!isValidDate) {
                  setDobError(t('coverage.errors.dob_error'));
                }
              } else if (dob) {
                setDobError(t('coverage.errors.dob_error'));
              }
            }}
          />

          {showPhoneField && (
            <PhoneNumberInput
              id="phoneNumber"
              value={phoneNumber}
              onChange={(value) => setPhoneNumber(value || '')}
              onBlur={() => {}}
              placeholder={t('coverage.sleepio.patient.phone_label')}
              error={false}
              helperText=""
              required={false}
              data-testid="phone-input"
            />
          )}

          {generalError && (
            <Typography color="error" style={{ marginTop: 8 }} data-testid="general-error">
              {generalError}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          rounded
          type="button"
          data-testid="check-coverage-button"
          disabled={!isFormValid || isSubmitting}
          onClick={handleSubmit}
          loading={isSubmitting}
          style={{ borderRadius: 100 }}
        >
          {t('coverage.check_coverage')}
        </Button>

        {/* TODO: Remove debug */}
        <Typography variant="caption" style={{ fontFamily: 'monospace', display: 'block', marginTop: 8 }}>
          [mount]
          {' '}
          {mountLog}
        </Typography>
        {debugLogs.length > 0 && (
          <Box
            mt={2}
            p={1}
            style={{
              backgroundColor: '#fff3cd',
              fontSize: 11,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            {debugLogs.map((log) => (
              <div key={log} style={{ marginTop: 2, color: log.includes('ERROR') ? 'red' : 'inherit' }}>
                {log}
              </div>
            ))}
          </Box>
        )}
        <ConsentWidget />
      </Container>
      <Footer />
    </Box>
  );
};

const ActivationEligibilityCheck: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <CoverageCheckInner />
  </ThemeProvider>
);

export default ActivationEligibilityCheck;
